// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useContext } from 'react';
import styled from '@emotion/styled';

import { useDashboardCalendarSettingPage } from './hooks';

import DashboardPageTemplate from 'presentations/views/components/templates/DashboardPageTemplate';
import {
  spacing,
  mediaMax,
  borderRadius,
  fontSize,
} from 'presentations/views/styles';
import {
  black,
  gray05,
  gray06,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import DashboardHeaderTitle from 'presentations/views/components/organisms/DashboardHeaderTitle';
import DashboardContentHeader from 'presentations/views/components/molecules/DashboardContentHeader';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import { ViewerItem } from 'presentations/api/schema/user';
import FormLabel from 'presentations/views/components/molecules/FormLabel';
import Button from 'presentations/views/components/atoms/Button';
import Radio from 'presentations/views/components/atoms/Radio';
import { flexibleStartWeekDay } from 'domains/models/event_type/startWeekDay';
import { PlanId } from 'domains/models/subscription/planId';

export interface Props {
  viewer?: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state: dashboardState } = useContext(DashboardPortalContext);
  const viewer = props.viewer || dashboardState.viewer!;
  const { state, actions } = useDashboardCalendarSettingPage({ viewer });

  return (
    <DashboardPageTemplate
      title={t('title')}
      viewer={viewer}
      contentHeader={
        <DashboardHeaderTitle
          title={t('parent title')}
          iconName="calendar-clock"
          onClickBack={state.canGoBack ? actions.onClickBack : undefined}
        />
      }
      content={
        <Container>
          <DashboardContentHeader title={t('title')} />
          <FormContainer>
            <FormField>
              <Header>
                <div>
                  <FormLabel
                    isPaid={true}
                    isShowPaidLink={!state.isValidStartWeekDay}
                    plan={PlanId.Team}
                  >
                    {t('startWeekDay')}
                  </FormLabel>
                  <Description>{t('startWeekDay description')}</Description>
                </div>
              </Header>
              <RadioList>
                <RadioListItem>
                  <Radio
                    name="startWeekDay"
                    isChecked={state.startWeekDay !== flexibleStartWeekDay}
                    onChange={actions.onChange}
                    label={t('Basic')}
                    isDisabled={!state.isValidStartWeekDay}
                  />
                  {state.startWeekDay !== flexibleStartWeekDay && (
                    <WeekDayList>
                      {[0, 1, 2, 3, 4, 5, 6].map((wday) => (
                        <WeekDayListItem
                          onClick={(e) => actions.onChangeStartWeekDay(wday)}
                          isActive={state.startWeekDay === wday}
                        >
                          {t(`week day ${wday}`)}
                        </WeekDayListItem>
                      ))}
                    </WeekDayList>
                  )}
                </RadioListItem>
                <RadioListItem>
                  <Radio
                    name="startWeekDay"
                    isChecked={state.startWeekDay === flexibleStartWeekDay}
                    label={t('Flexible')}
                    onChange={actions.onChange}
                    value={flexibleStartWeekDay}
                    isDisabled={!state.isValidStartWeekDay}
                  />
                </RadioListItem>
              </RadioList>
            </FormField>
            <ButtonContainer>
              <SubmitButton
                size={'large'}
                type="submit"
                color="primary"
                onClick={actions.onSubmit}
                isDisabled={state.isInitLoading || !state.isValidStartWeekDay}
                isLoading={state.isLoading}
              >
                {t('Submit')}
              </SubmitButton>
            </ButtonContainer>
          </FormContainer>
        </Container>
      }
    />
  );
}

const Container = styled.div`
  margin-top: ${spacing(24)};
`;
const FormContainer = styled.div`
  background: ${white};
  border-radius: ${borderRadius(16)};
  padding: ${spacing(48)};
  ${mediaMax.tablet} {
    width: 100%;
    padding: ${spacing(24)};
  }
  ${mediaMax.smartphone} {
    padding: ${spacing(16)};
  }
`;

const FormField = styled.div`
  border-bottom: 1px solid ${gray05};
  margin-bottom: ${spacing(32)};
`;
const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;
const Description = styled.div`
  font-size: ${fontSize(12)};
  margin-bottom: ${spacing(16)};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;
const SubmitButton = styled(Button)`
  width: 240px;

  ${mediaMax.tablet} {
    width: 140px;
  }
`;

const RadioList = styled.ul``;
const RadioListItem = styled.li``;

const WeekDayList = styled.ul`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(16)};
`;
const WeekDayListItem = styled.li<{ isActive: boolean }>`
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${(props) => (props.isActive ? primary : gray06)};
  color: ${(props) => (props.isActive ? whiteFix : black)};
  margin-right: ${spacing(4)};
  font-size: ${fontSize(11)};
  :last-of-type {
    margin-right: 0;
  }
`;
